/**
 * Simplified Monaco SQL setup without ANTLR
 * Based on Databricks universe repo approach
 */

import { Monaco } from '@monaco-editor/react';
import { languages } from 'monaco-editor';
import { SchemaProvider } from '../autocomplete/types';
import {
  nonTypeKeywords,
  nonTypeBuiltinFunctions,
  typeKeywords,
  literals,
} from '../../../lib/sql-reference/tokenReference';
import {
  BUILT_IN_FUNCTION_NAMES,
  TABLE_VALUED_FUNCTION_NAMES,
} from '../../../lib/sql-reference/builtInFunctionNames';

/**
 * SQL language configuration
 */
export const sqlConf: languages.LanguageConfiguration = {
  wordPattern: /\*|(-?\d*\.\d\w*)|([^`~!@#%^&*()\-=+[{\]}\\|;:'",.<>/?\s]+)/g,

  comments: {
    lineComment: '--',
    blockComment: ['/*', '*/'],
  },

  brackets: [
    ['{', '}'],
    ['[', ']'],
    ['(', ')'],
  ],

  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
    { open: '`', close: '`' },
  ],

  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
    { open: '`', close: '`' },
  ],
};

/**
 * SQL Monarch tokenization rules
 */
export const sqlLanguage: languages.IMonarchLanguage = {
  defaultToken: '',
  tokenPostfix: '.sql',
  ignoreCase: true,

  brackets: [
    { open: '[', close: ']', token: 'delimiter.square' },
    { open: '(', close: ')', token: 'delimiter.parenthesis' },
  ],

  keywords: nonTypeKeywords,
  literals,
  functions: nonTypeBuiltinFunctions,
  types: typeKeywords,

  tokenizer: {
    root: [
      { include: '@comments' },
      { include: '@whitespace' },
      { include: '@numbers' },
      { include: '@strings' },
      { include: '@delimitedIdentifier' },
      [/[;,.]/, 'delimiter'],
      [/[()]/, '@brackets'],

      // Functions (check before keywords)
      [
        /\b\w+\(/,
        {
          cases: {
            '@functions': 'predefined',
          },
        },
      ],

      // Keywords, types, literals, and identifiers
      [
        /[\w@#$]+/,
        {
          cases: {
            '@literals': 'literal',
            '@keywords': 'keyword',
            '@types': 'type',
            '@default': 'identifier',
          },
        },
      ],

      // Operators
      [/[<>=!%&+\-*/|~^]/, 'operator'],
    ],

    whitespace: [[/\s+/, 'white']],

    comments: [
      [/--+.*/, 'comment'],
      [/\/\*/, { token: 'comment.quote', next: '@comment' }],
    ],

    comment: [
      [/[^*/]+/, 'comment'],
      [/\*\//, { token: 'comment.quote', next: '@pop' }],
      [/./, 'comment'],
    ],

    numbers: [
      [/0[xX][0-9a-fA-F]*/, 'number.hex'],
      [/[$][+-]*\d*(\.\d*)?/, 'number.currency'],
      [/((\d+(\.\d*)?)|(\.\d+))([eE][-+]?\d+)?/, 'number.float'],
    ],

    strings: [
      [/'/, { token: 'string.quote', next: '@string' }],
      [/"/, { token: 'string.double.quote', next: '@stringDouble' }],
    ],

    string: [
      [/[^']+/, 'string'],
      [/''/, 'string'],
      [/'/, { token: 'string.quote', next: '@pop' }],
    ],

    stringDouble: [
      [/[^"]+/, 'string.double'],
      [/""/, 'string.double'],
      [/"/, { token: 'string.double.quote', next: '@pop' }],
    ],

    delimitedIdentifier: [
      [/`/, { token: 'identifier.quote', next: '@quotedIdentifier' }],
    ],

    quotedIdentifier: [
      [/[^`]+/, 'identifier'],
      [/``/, 'identifier'],
      [/`/, { token: 'identifier.quote', next: '@pop' }],
    ],
  },
};

/**
 * Setup simplified SQL language for Monaco
 */
export function setupSimpleSql(
  monaco: Monaco,
  schemaProvider?: SchemaProvider,
) {
  // Register the language
  monaco.languages.register({ id: 'databricks-sql' });

  // Set configuration
  monaco.languages.setLanguageConfiguration('databricks-sql', sqlConf);

  // Set tokenization rules
  monaco.languages.setMonarchTokensProvider('databricks-sql', sqlLanguage);

  // Register autocomplete provider
  monaco.languages.registerCompletionItemProvider('databricks-sql', {
    triggerCharacters: ['.', ' '],
    provideCompletionItems: async (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const suggestions: languages.CompletionItem[] = [];
      const wordUpper = word.word.toUpperCase();

      // Add keywords
      nonTypeKeywords.forEach((keyword) => {
        if (keyword.startsWith(wordUpper)) {
          suggestions.push({
            label: keyword,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: keyword,
            range,
          });
        }
      });

      // Add data types
      typeKeywords.forEach((type) => {
        if (type.startsWith(wordUpper)) {
          suggestions.push({
            label: type,
            kind: monaco.languages.CompletionItemKind.TypeParameter,
            insertText: type,
            range,
          });
        }
      });

      // Add functions
      [...BUILT_IN_FUNCTION_NAMES, ...TABLE_VALUED_FUNCTION_NAMES].forEach(
        (func) => {
          if (func.startsWith(wordUpper)) {
            suggestions.push({
              label: func,
              kind: monaco.languages.CompletionItemKind.Function,
              insertText: func + '()',
              insertTextRules:
                monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range,
            });
          }
        },
      );

      // Add schema tables
      if (schemaProvider) {
        const tables = await schemaProvider.getTables();
        tables.forEach((table) => {
          suggestions.push({
            label: table.name,
            kind: monaco.languages.CompletionItemKind.Class,
            insertText: table.name,
            detail: table.schema ? `${table.schema}.${table.name}` : table.name,
            documentation: table.comment,
            range,
          });
        });
      }

      // Context-aware column suggestions
      const textUntilPosition = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      // Check if we're after a table name (e.g., "table.")
      const tableMatch = textUntilPosition.match(/(\w+)\.$/);
      if (tableMatch && schemaProvider) {
        const tableName = tableMatch[1];
        const columns = await schemaProvider.getColumns(tableName);
        columns.forEach((column) => {
          suggestions.push({
            label: column.name,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: column.name,
            detail: column.type,
            documentation: column.comment,
            range,
          });
        });
      }

      return { suggestions };
    },
  });
}
